import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { State } from '../models/Interfaces/company';
import moment from 'moment';

export const viewResponseDocument = (documentData: {
  documentContentAsString: string;
  documentType: string;
}) => {
  const byteCharacters = atob(documentData.documentContentAsString);
  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  const byteArray = new Uint8Array(byteNumbers);
  const blob = new Blob([byteArray], {
    type: getType(documentData.documentType),
  });

  // Create a URL for the Blob
  const blobUrl = URL.createObjectURL(blob);

  // Open the PDF in a new tab
  window.open(blobUrl, '_blank');
};

const getType = (documentType: string): string => {
  switch (documentType.toLowerCase()) {
    case 'pdf':
      return 'application/pdf';
    case 'jpg':
    case 'jpeg':
      return 'image/jpeg';
    case 'png':
      return 'image/png';
    case 'doc':
      return 'application/msword';
    case 'docx':
      return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
    case 'xls':
      return 'application/vnd.ms-excel';
    case 'xlsx':
      return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
    default:
      return 'application/octet-stream'; // Fallback type
  }
};

export const downloadResponseDocument = (
  documentData: { documentContentAsString: string; documentType: string },
  fileName: string,
) => {
  try {
    const byteCharacters = atob(documentData.documentContentAsString);
    const byteNumbers = new Array(byteCharacters.length);

    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }

    const byteArray = new Uint8Array(byteNumbers);

    const blob = new Blob([byteArray], {
      type: getType(documentData.documentType),
    });

    // Create a link element
    const link = document.createElement('a');

    // Create a URL for the Blob and set it as the href attribute
    const url = window.URL.createObjectURL(blob);
    link.href = url;

    // Set the download attribute with the desired file name and extension
    link.download = `${fileName}`;
    // Append the link to the document body and trigger the download
    document.body.appendChild(link);
    link.click();

    // Clean up by removing the link and revoking the Object URL
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Error downloading the document:', error);
  }
};

// convert 31/08/2024 to 2024-08-31T00:00:00
export const convertDate = (inputDate: string): string => {
  const timeRegex = /^\d{2}\/\d{2}\/\d{4}$/;
  if (timeRegex.test(inputDate)) {
    const [month, day, year] = inputDate?.split('/').map(Number);
    const date = new Date(year, month - 1, day + 1);
    return date?.toISOString()?.split('T')[0] + 'T00:00:00';
  }
  return inputDate;
};

export const formatDateToISO = (date: any): string => {
  const selectedDate = new Date(date);
  return `${selectedDate.getFullYear()}-${(selectedDate.getMonth() + 1)
    .toString()
    .padStart(
      2,
      '0',
    )}-${selectedDate.getDate().toString().padStart(2, '0')}T00:00:00`;
};

export const formatDateTime = (date: Date, time: string): string => {
  const [hours, minutes] = time.split(':').map(Number);
  const selectedDate = new Date(date);
  selectedDate.setHours(hours);
  selectedDate.setMinutes(minutes);
  selectedDate.setSeconds(0);

  const year = selectedDate.getFullYear();
  const month = (selectedDate.getMonth() + 1).toString().padStart(2, '0');
  const day = selectedDate.getDate().toString().padStart(2, '0');
  const hrs = selectedDate.getHours().toString().padStart(2, '0');
  const mins = selectedDate.getMinutes().toString().padStart(2, '0');
  const seconds = selectedDate.getSeconds().toString().padStart(2, '0');

  return `${year}-${month}-${day}T${hrs}:${mins}:${seconds}`;
};

export const convertStringToObject = (data: any) => {
  try {
    return typeof data === 'string' ? JSON.parse(data) : data;
  } catch (error) {
    return null;
  }
};

// convert 2024-08-31T00:00:00 to 31/08/2024
export const formatDateWithoutTime = (value: string) => {
  const isoDateRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}$/;
  if (isoDateRegex.test(value)) {
    const [year, month, day] = value.split('T')[0].split('-');
    return `${month}/${day}/${year}`;
  }
  return value;
};

export const formatStateRegistrationDate = (date: any): string => {
  if (moment.isMoment(date)) {
    return date.startOf('month').format('YYYY-MM-DD');
  }
  if (typeof date === 'string' && date) {
    const [month, year] = date.split('/').map(Number);
    if (isNaN(month) || isNaN(year) || month < 1 || month > 12) {
      return date;
    }
    const dateObj = new Date(year, month - 1, 1);
    return dateObj.toISOString().split('T')[0];
  }
  return '';
};

export function exactLengthValidator(
  length: number,
  isNumeric: boolean = false,
): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.value) {
      return null;
    }
    const inputValue = isNumeric
      ? String(control.value).replace(/\D+/g, '')
      : control.value;
    return inputValue.length === length
      ? null
      : {
          exactLength: {
            requiredLength: length,
            actualLength: inputValue.length,
          },
        };
  };
}

export function fileSizeValidator(maxSize: number): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const file = control.value;
    if (file && file.size > maxSize) {
      return { fileSize: true };
    }
    return null;
  };
}

export function formatPhoneNumber(value: string): string {
  const phoneNumber = value.replace(/\D+/g, '');
  const phoneNumberLength = phoneNumber.length;
  if (phoneNumberLength === 0) return '';
  if (phoneNumberLength < 4) return `(${phoneNumber}`;
  if (phoneNumberLength < 7)
    return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
  return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6)}`;
}

export function getUSStates(): State[] {
  return [
    { name: 'Alabama', abbreviation: 'AL' },
    { name: 'Alaska', abbreviation: 'AK' },
    { name: 'Arizona', abbreviation: 'AZ' },
    { name: 'Arkansas', abbreviation: 'AR' },
    { name: 'California', abbreviation: 'CA' },
    { name: 'Colorado', abbreviation: 'CO' },
    { name: 'Connecticut', abbreviation: 'CT' },
    { name: 'District of Columbia', abbreviation: 'DC' },
    { name: 'Delaware', abbreviation: 'DE' },
    { name: 'Florida', abbreviation: 'FL' },
    { name: 'Georgia', abbreviation: 'GA' },
    { name: 'Hawaii', abbreviation: 'HI' },
    { name: 'Idaho', abbreviation: 'ID' },
    { name: 'Illinois', abbreviation: 'IL' },
    { name: 'Indiana', abbreviation: 'IN' },
    { name: 'Iowa', abbreviation: 'IA' },
    { name: 'Kansas', abbreviation: 'KS' },
    { name: 'Kentucky', abbreviation: 'KY' },
    { name: 'Louisiana', abbreviation: 'LA' },
    { name: 'Maine', abbreviation: 'ME' },
    { name: 'Maryland', abbreviation: 'MD' },
    { name: 'Massachusetts', abbreviation: 'MA' },
    { name: 'Michigan', abbreviation: 'MI' },
    { name: 'Minnesota', abbreviation: 'MN' },
    { name: 'Mississippi', abbreviation: 'MS' },
    { name: 'Missouri', abbreviation: 'MO' },
    { name: 'Montana', abbreviation: 'MT' },
    { name: 'Nebraska', abbreviation: 'NE' },
    { name: 'Nevada', abbreviation: 'NV' },
    { name: 'New Hampshire', abbreviation: 'NH' },
    { name: 'New Jersey', abbreviation: 'NJ' },
    { name: 'New Mexico', abbreviation: 'NM' },
    { name: 'New York', abbreviation: 'NY' },
    { name: 'North Carolina', abbreviation: 'NC' },
    { name: 'North Dakota', abbreviation: 'ND' },
    { name: 'Ohio', abbreviation: 'OH' },
    { name: 'Oklahoma', abbreviation: 'OK' },
    { name: 'Oregon', abbreviation: 'OR' },
    { name: 'Pennsylvania', abbreviation: 'PA' },
    { name: 'Rhode Island', abbreviation: 'RI' },
    { name: 'South Carolina', abbreviation: 'SC' },
    { name: 'South Dakota', abbreviation: 'SD' },
    { name: 'Tennessee', abbreviation: 'TN' },
    { name: 'Texas', abbreviation: 'TX' },
    { name: 'Utah', abbreviation: 'UT' },
    { name: 'Vermont', abbreviation: 'VT' },
    { name: 'Virginia', abbreviation: 'VA' },
    { name: 'Washington', abbreviation: 'WA' },
    { name: 'West Virginia', abbreviation: 'WV' },
    { name: 'Wisconsin', abbreviation: 'WI' },
    { name: 'Wyoming', abbreviation: 'WY' },
  ];
}
